import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { 
  Heart, 
  Share2, 
  MessageCircle, 
  Eye, 
  FileText, 
  Code,
  DollarSign,
  Loader2,
  Check,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { projectAPI, paymentAPI } from '../lib/api';
import { MONEY_BOOST_OPTIONS, LEGAL_DISCLAIMER } from '../lib/utils';

export function BoostButtons({ project, userBoosts = [], onBoostComplete }) {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [moneyOpen, setMoneyOpen] = useState(false);

  const isCreator = user?.user_id === project.creator_id;
  const hasLiked = userBoosts.includes('like');
  const hasShared = userBoosts.includes('share');

  const handleBoost = async (boostType, amount = null) => {
    if (!isAuthenticated) {
      toast.error('Please log in to boost this project');
      return;
    }

    if (isCreator) {
      toast.error("You can't boost your own project");
      return;
    }

    setLoading(boostType);

    try {
      const response = await projectAPI.boost(project.project_id, {
        boost_type: boostType,
        amount: amount
      });

      toast.success(response.data.message, {
        description: `+${response.data.score_added.toFixed(1)} momentum`
      });

      onBoostComplete?.(response.data.project);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to boost';
      toast.error(message);
    } finally {
      setLoading(null);
    }
  };

  const handleShare = async () => {
    // First do the share action
    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: project.tagline,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or error
        if (err.name !== 'AbortError') {
          // Copy to clipboard as fallback
          await navigator.clipboard.writeText(window.location.href);
          toast.success('Link copied to clipboard!');
        }
        return;
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }

    // Record the share boost
    if (isAuthenticated && !hasShared && !isCreator) {
      handleBoost('share');
    }
  };

  return (
    <div className="space-y-4">
      {/* Free Boosts */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => handleBoost('like')}
          disabled={loading === 'like' || hasLiked}
          className={`boost-button ${hasLiked ? 'active' : ''}`}
          data-testid="boost-like-btn"
        >
          {loading === 'like' ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : hasLiked ? (
            <Check className="w-5 h-5 text-orange-500" />
          ) : (
            <Heart className="w-5 h-5" />
          )}
          <span>{hasLiked ? 'Liked' : 'Like'}</span>
          <span className="text-xs text-slate-400">+1</span>
        </button>

        <button
          onClick={handleShare}
          disabled={loading === 'share'}
          className={`boost-button ${hasShared ? 'active' : ''}`}
          data-testid="boost-share-btn"
        >
          {loading === 'share' ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : hasShared ? (
            <Check className="w-5 h-5 text-orange-500" />
          ) : (
            <Share2 className="w-5 h-5" />
          )}
          <span>{hasShared ? 'Shared' : 'Share'}</span>
          <span className="text-xs text-slate-400">+2</span>
        </button>

        <button
          onClick={() => setCommentOpen(true)}
          className="boost-button"
          data-testid="boost-comment-btn"
        >
          <MessageCircle className="w-5 h-5" />
          <span>Comment</span>
          <span className="text-xs text-slate-400">+3</span>
        </button>
      </div>

      {/* Time Boosts */}
      <div className="flex flex-wrap gap-3">
        {project.demo_url && (
          <a
            href={project.demo_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isAuthenticated && !isCreator && handleBoost('read')}
            className="boost-button"
            data-testid="boost-demo-btn"
          >
            <Eye className="w-5 h-5" />
            <span>View Demo</span>
            <ExternalLink className="w-3 h-3 opacity-50" />
          </a>
        )}

        {project.github_url && (
          <a
            href={project.github_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isAuthenticated && !isCreator && handleBoost('review')}
            className="boost-button"
            data-testid="boost-github-btn"
          >
            <Code className="w-5 h-5" />
            <span>Review Code</span>
            <span className="text-xs text-slate-400">+10</span>
          </a>
        )}
      </div>

      {/* Money Boost */}
      <Button
        onClick={() => setMoneyOpen(true)}
        className="w-full btn-boost text-lg py-6"
        disabled={isCreator}
        data-testid="boost-money-btn"
      >
        <DollarSign className="w-5 h-5 mr-2" />
        Support with Money
      </Button>

      {/* Comment Dialog */}
      <CommentDialog
        open={commentOpen}
        onOpenChange={setCommentOpen}
        projectId={project.project_id}
        onSubmit={onBoostComplete}
      />

      {/* Money Dialog */}
      <MoneyDialog
        open={moneyOpen}
        onOpenChange={setMoneyOpen}
        project={project}
        onComplete={onBoostComplete}
      />
    </div>
  );
}

function CommentDialog({ open, onOpenChange, projectId, onSubmit }) {
  const [content, setContent] = useState('');
  const [isFeedback, setIsFeedback] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [strengths, setStrengths] = useState('');
  const [risks, setRisks] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (content.length < 10) {
      toast.error('Comment must be at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      await projectAPI.addComment(projectId, {
        content,
        is_feedback: isFeedback,
        is_review: isReview,
        strengths: strengths.split(',').map(s => s.trim()).filter(Boolean),
        risks: risks.split(',').map(s => s.trim()).filter(Boolean)
      });

      toast.success('Comment added!', {
        description: isReview ? '+8 momentum' : isFeedback ? '+5 momentum' : '+3 momentum'
      });

      setContent('');
      setIsFeedback(false);
      setIsReview(false);
      setStrengths('');
      setRisks('');
      onOpenChange(false);
      onSubmit?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a Comment</DialogTitle>
          <DialogDescription>
            Quality comments earn more momentum points.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            placeholder="Share your thoughts, feedback, or review..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            data-testid="comment-textarea"
          />

          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="feedback"
                checked={isFeedback}
                onCheckedChange={setIsFeedback}
              />
              <Label htmlFor="feedback" className="text-sm">
                This is constructive feedback (+5 pts)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="review"
                checked={isReview}
                onCheckedChange={setIsReview}
              />
              <Label htmlFor="review" className="text-sm">
                This is a technical review (+8 pts)
              </Label>
            </div>
          </div>

          {isReview && (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="strengths" className="text-sm">
                  Strengths (comma-separated)
                </Label>
                <input
                  id="strengths"
                  type="text"
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                  placeholder="e.g., Clean code, Good UX, Innovative"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="risks" className="text-sm">
                  Risks / Areas for Improvement
                </Label>
                <input
                  id="risks"
                  type="text"
                  value={risks}
                  onChange={(e) => setRisks(e.target.value)}
                  placeholder="e.g., Scalability, Security"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleSubmit}
            disabled={loading || content.length < 10}
            className="w-full"
            data-testid="submit-comment-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Post Comment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MoneyDialog({ open, onOpenChange, project, onComplete }) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  const handlePayment = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to support this project');
      return;
    }

    if (!selectedAmount) {
      toast.error('Please select an amount');
      return;
    }

    setLoading(true);

    try {
      const response = await paymentAPI.createSession({
        project_id: project.project_id,
        amount: selectedAmount,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment session');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Support with a Micro-Grant</DialogTitle>
          <DialogDescription>
            Money has logarithmic impact - community matters more.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Amount Options */}
          <div className="grid grid-cols-5 gap-2">
            {MONEY_BOOST_OPTIONS.map((option) => (
              <button
                key={option.amount}
                onClick={() => setSelectedAmount(option.amount)}
                className={`money-option p-3 rounded-xl border-2 text-center transition-all ${
                  selectedAmount === option.amount
                    ? 'selected'
                    : 'border-slate-200 hover:border-orange-300'
                }`}
                data-testid={`money-option-${option.amount}`}
              >
                <div className="font-bold text-lg">{option.label}</div>
                <div className="text-xs opacity-70">+{option.score} pts</div>
              </button>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="disclaimer-banner">
            <p>{LEGAL_DISCLAIMER}</p>
          </div>

          <Button
            onClick={handlePayment}
            disabled={!selectedAmount || loading}
            className="w-full btn-boost"
            data-testid="proceed-payment-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {selectedAmount ? `Support with $${selectedAmount}` : 'Select an amount'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
